import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import {
  MdAccessTime,
  MdCheckCircle,
  MdChevronLeft,
  MdChevronRight,
  MdCancel,
  MdChurch,
  MdEvent,
  MdExpandMore,
  MdLocalShipping,
  MdLocationOn,
  MdMap,
  MdPerson,
  MdPhone,
  MdReceiptLong,
  MdRepeat,
  MdStorefront,
} from "react-icons/md";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { can } from "../auth/permissions";
import { replaceCharacter } from "../components";
import { closeRunBuilder, forgetRunBuilder, openRunBuilder } from "../runBuilderWindow";
import { casketTypesOf, lineGoesWithCasket } from "../productCategories";
import OrderDetailPanel, {
  transferProvenance,
} from "../components/OrderDetailPanel";
import CalendarMonth, {
  startOfDay,
  shiftDays,
  shiftMonth,
  monthOf,
} from "../components/CalendarMonth";
import "../work-order-list.css";

/* ------------------------------------------------------------------ *
 * Work orders (redesign) — /work-orders
 *
 * Replaces workOrderListPage.js:
 *   - two stacked tables (funeral + retail) -> one unified table
 *   - a bare date strip                     -> day rail
 *   - `makeRunSheet` popup + localStorage   -> selection mode here, the
 *     builder popped out on /run-sheet/new and fed live through the
 *     "rf-run-draft" localStorage key
 *   - side slide detail                     -> OrderDetailPanel
 *
 * Wired to the same endpoints as the legacy page: POST /wo/getAllBy/
 * (one call per order type), POST /run/getAllBy for the day's saved runs
 * (the products already committed to one), POST /wo/check-complete for
 * row status, GET /wo/getProductsByOrderId/:id for the detail panel.
 * ------------------------------------------------------------------ */

const DATE_FMT = "DD/MM/YYYY";
const RAIL_DAYS = 15; // days visible in the rail (odd, selected in the middle)
const RAIL_HALF = Math.floor(RAIL_DAYS / 2);
const RAIL_STEP = 7; // days each arrow click pages the rail by
const RAIL_BUFFER = 2 * RAIL_STEP; // extra days rendered off-screen on each side for sliding
const RAIL_SLIDE_MS = 320;
const safeParse = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const DRAFT_KEY = "rf-run-draft";

/* One colour per saved run sheet of the day (legacy ThemeColor cue): the
   run's card and its products down in the list share it. */
const RUN_COLORS = [
  "#b0872f",
  "#3f7f8c",
  "#5a5fa8",
  "#7a8a2e",
  "#9a4f8f",
  "#4d7a52",
  "#b3563c",
  "#55677a",
];
const NEW_RUN_COLOR = "#3f9d4d"; // the run being built — matches picked chips
const PROMPT_W = 300; // the type bubble, sized like the legacy list's
const PROMPT_H = 92;

/* A chip's group name vs the product_type the run sheet saves it under —
   the three labels buildDraft() posts, lowercased. Retail's "all" group is
   the one that differs, so without this map committed retail products
   stayed pickable and could be allocated to a second run. */
const GROUP_SAVED_TYPE = {
  "casket cover": "casket cover",
  tributes: "tributes",
  all: "flower arrangements",
};
const savedType = (group) =>
  GROUP_SAVED_TYPE[group] || String(group || "").toLowerCase();
/* One key per product already on a run: group + product, both lowercased.
   "<group>|*" stands for a whole group, used for older runs whose
   deliveries carry no readable product list. */
const takenKey = (group, key) =>
  `${savedType(group)}|${String(key || "*").toLowerCase()}`;
const runColor = (i) => RUN_COLORS[i % RUN_COLORS.length];
const isExtra = (label) => /pedstals|pedestals|easels/i.test(label);

const locName = (raw) => (safeParse(raw, {}) || {}).name || "";

/* Where each product group of an order is delivered — the field the run
   draft reads for that group. A group with no saved destination can't go
   on a run sheet: the driver would have nowhere to take it. */
const GROUP_LOCATION = {
  "casket cover": (o) => o.casket_cover_delivery_location,
  tributes: (o) => o.tribuite_delivery_location,
  all: (o) => o.delivery_location,
};
const groupHasLocation = (order, group) =>
  Boolean(locName((GROUP_LOCATION[group] || (() => ""))(order)));
const GROUP_LABEL = {
  "casket cover": "casket cover",
  tributes: "tribute",
  all: "flower",
};

/* How a product is identified everywhere on this page: casket covers by
   their code, everything else by type. Shared with the taken map so a
   product on a saved run matches the chip that stands for it. */
const productKey = (p) => {
  const type = String(p.product_type || "");
  return type.toLowerCase() === "casket cover"
    ? p.code || type
    : type || p.product_name || p.name || "Product";
};

/* Legacy shorten_product_list, kept verbatim in spirit: casket covers are
   grouped by code and every other product by type, amounts summed —
   "CC302 x 1", "Wreath x 3". Each chip remembers the products it stands
   for so selection mode can hand them to the builder. */
function groupProducts(list, group) {
  const order = [];
  const byKey = {};
  (list || []).forEach((p) => {
    const key = productKey(p);
    if (!byKey[key]) {
      byKey[key] = { key, amount: 0, products: [], group };
      order.push(key);
    }
    byKey[key].amount += Number(p.amount) || 1;
    byKey[key].products.push(p);
  });
  return order.map((k) => ({ ...byKey[k], label: `${k} x ${byKey[k].amount}` }));
}

/* Split a funeral order's products into casket-cover vs tribute groups by
   product type — Settings → Product types decides what goes with the casket
   cover (src/productCategories.js) — except a line added in the Tributes
   half of the order form (`as_tribute`), which is a tribute whatever its
   type. The group is also the run sheet stop it rides to. */
function buildChips(order, ccTypes) {
  if (order.type === "Funeral") {
    const all = (safeParse(order.casket_cover_selected, []) || []).concat(
      safeParse(order.tributes_selected, []) || []
    );
    const withCasket = (p) => lineGoesWithCasket(p, ccTypes);
    return {
      cc: groupProducts(all.filter(withCasket), "casket cover"),
      tributes: groupProducts(all.filter((p) => !withCasket(p)), "tributes"),
    };
  }
  return { all: groupProducts(safeParse(order.products_selected, []) || [], "all") };
}

/* One display row per work order, from the raw API document. Column
   fields mirror the legacy TableList cells one-to-one. */
function buildRow(order, ccTypes) {
  const funeral = order.type === "Funeral";
  const time = funeral
    ? order.casket_cover_delivery_time || order.funeral_time || ""
    : order.time_of_delivery || "";
  const fdCode = (safeParse(order.fd_code, {}) || {}).code || "";
  const unpaid = order.payment_option === "**UNPAID**";
  /* A payment date means it was paid — card payments carry one from the
     form, and the preview's "Paid" button stamps one on any method. */
  const paid = Boolean(order.payment_date) && !unpaid;
  const groups = buildChips(order, ccTypes);
  const chips = funeral ? groups.cc.concat(groups.tributes) : groups.all;
  chips.forEach((c, i) => {
    c.index = i;
  });
  return {
    id: order._id,
    serial: order.serial_no || order._id,
    type: order.type,
    time: time || "—",
    name: funeral
      ? order.late_person_name || "—"
      : order.company_name || order.recipient_name || "—",
    fdCode,
    unpaid,
    paid,
    invoice: order.invoice_number || "N/A",
    orderedBy: order.ordered_by || "",
    chips,
    groups,
    /* funeral columns */
    ccTime: order.casket_cover_delivery_time || "",
    funeralLocation: locName(order.funeral_location) || "N/A",
    funeralWhen: [order.funeral_date, order.funeral_time].filter(Boolean).join(" "),
    ccDelivery: locName(order.casket_cover_delivery_location) || "N/A",
    /* retail columns */
    company: order.company_name || "",
    frequency: order.frequency || "",
    recipientName: order.recipient_name || "",
    recipientPhone: order.recipient_phone || "",
    deliveryArea: order.delivery_area || "N/A",
    deliveryTime: order.time_of_delivery || "",
    deliveryLocation: locName(order.delivery_location) || "N/A",
    transferredFrom: transferProvenance(order),
    raw: order,
  };
}

function RowCurve() {
  /* The legacy list's swoosh, stretched across the whole card as a soft
     background wash — the content sits on a layer above it. The top edge
     used to be a cubic that climbed to the right; it cut diagonally through
     the product chips, so it now runs flat across from the same left-hand
     anchor (y=131.09). The lower edge stays where it was — it sits below
     the 150-unit viewBox and is clipped either way. */
  return (
    <div className="wo-curve" aria-hidden="true">
      <svg viewBox="0 0 500 150" preserveAspectRatio="none">
        <path
          d="M-1.56,131.09 L601.56,131.09 L532.18,186.09 L-2.18,154.09 Z"
          fill="#fbf8f0"
          stroke="#f4e3bf"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

/* Icon-labelled fact pill. The label under the icon replaces the old
   column header, so rows read on their own. */
function Fact({ icon, k, v, tone }) {
  const empty = v === "" || v === null || v === undefined || v === "N/A";
  return (
    <span className={`wo-fact${tone ? ` is-${tone}` : ""}${empty ? " is-empty" : ""}`}>
      <span className="wo-fact-ic">{icon}</span>
      <span className="wo-fact-txt">
        <span className="wo-fact-k">{k}</span>
        <span className="wo-fact-v">{empty ? "—" : v}</span>
      </span>
    </span>
  );
}

const STATUS_META = {
  complete: { label: "Complete", color: "#4d7a52" },
  open: { label: "Open", color: "#c4bdad" },
};

/* Build the detail-panel view model from the raw order + the per-product
   progress fetch (GET /wo/getProductsByOrderId/:id). */
function buildDetail(row, products, status) {
  const o = row.raw;
  const funeral = row.type === "Funeral";
  const receivedBy = (safeParse(o.received_by, {}) || {}).full_name || "";

  const detailProducts = (products || []).map((p, i) => {
    const progressAll = Array.isArray(p.orderProgress) ? p.orderProgress : [];
    const progress = funeral
      ? progressAll
      : progressAll.filter((a) => a.delivery_date === o.delivery_date);
    const started = progress.length > 0;
    const finished = started && progress.every((a) => a.ended);
    return {
      type: p.product_name || p.name || p.product_type || "Product",
      code: p.code || "",
      size: p.size || "",
      amount: Number(p.amount) || 1,
      price: Number(p.customPrice ? p.customPrice : p.price) || 0,
      /* what the bench needs to read without opening print or edit */
      sashPrice: Number(p.sashPrice) || 0,
      sash: p.sash_message || "",
      comment: p.comment || "",
      lidType: p.lidType || "",
      assigned: (progress[0] || {}).assigned_to
        ? progress[0].assigned_to.full_name
        : "",
      state: finished ? "Checked" : started ? "Arranging" : "Not started",
      color: ["var(--rf-brand)", "var(--rf-brand-lite)", "var(--rf-brand-soft)", "var(--rf-brand-pale)"][i % 4],
      _starts: progress.map((a) => Number(a.start)).filter(Boolean),
    };
  });

  const anyStarted = detailProducts.some((p) => p.state !== "Not started");
  const allChecked =
    detailProducts.length > 0 && detailProducts.every((p) => p.state === "Checked");
  const firstStart = Math.min(
    ...detailProducts.flatMap((p) => p._starts).concat([Infinity])
  );

  const service = funeral
    ? [
        {
          k: "Service",
          v: [o.funeral_date, o.funeral_time].filter(Boolean).join(" · ") || "—",
        },
        { k: "Location", v: locName(o.funeral_location) || "—" },
        {
          k: "Casket cover to",
          v: locName(o.casket_cover_delivery_location)
            ? `${locName(o.casket_cover_delivery_location)}${
                o.casket_cover_delivery_time ? ` — by ${o.casket_cover_delivery_time}` : ""
              }`
            : "—",
        },
        ...(locName(o.tribuite_delivery_location)
          ? [{ k: "Tributes to", v: locName(o.tribuite_delivery_location) }]
          : []),
      ]
    : [
        {
          k: "Delivery",
          v: [o.delivery_date, o.time_of_delivery].filter(Boolean).join(" · ") || "—",
        },
        { k: "Location", v: locName(o.delivery_location) || "—" },
        {
          k: "Recipient",
          v: [o.recipient_name, o.recipient_phone].filter(Boolean).join(" · ") || "—",
        },
        ...(o.frequency ? [{ k: "Frequency", v: o.frequency }] : []),
      ];

  const payment = [
    funeral
      ? { k: "Funeral director", v: row.fdCode || "—" }
      : { k: "Customer", v: o.company_name || o.recipient_name || "—" },
    /* the contact number sits under the name, so the shop can call back */
    { k: "Ordered by", v: o.ordered_by || "—", sub: o.orderer_phone || "" },
    row.unpaid
      ? { k: "Invoice", v: "Not entered — shows as UNPAID", tone: "danger" }
      : {
          k: "Invoice",
          v: `${o.invoice_number || "N/A"}${
            row.paid ? ` · paid ${o.payment_date}` : ""
          }`,
        },
    { k: "Method", v: row.unpaid ? "—" : o.payment_option || "—" },
  ];

  return {
    provenance: [
      funeral ? row.fdCode : o.company_name,
      o.ordered_by ? `ordered by ${o.ordered_by}` : "",
      receivedBy ? `received by ${receivedBy}` : "",
    ]
      .filter(Boolean)
      .join(" · "),
    created: o.date || "",
    discardOn: o.date
      ? moment(o.date, DATE_FMT).add(7, "years").format("DD MMM YYYY")
      : "",
    timeline: {
      received: { done: true, at: o.date || "—" },
      arranging: anyStarted
        ? {
            done: allChecked,
            active: !allChecked,
            at: isFinite(firstStart)
              ? moment(firstStart).format("HH:mm")
              : "started",
          }
        : {},
      checked:
        status === "complete" || allChecked
          ? { done: true, at: "" }
          : {},
      delivered: { at: row.time !== "—" ? `due ${row.time}` : "—" },
    },
    service,
    payment,
    products: detailProducts,
  };
}

export function WorkOrdersRedesign() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  /* Day rail: a full-width window of RAIL_DAYS days centred on `railCenter`.
     Clicking a day selects it and re-centres the window on it; the arrows
     page the window without touching the selection, and the calendar
     popover jumps straight to any date.

     Sliding: the strip is rendered RAIL_BUFFER days wider on each side
     around `renderCenter` and offset with a CSS transition. When the
     centre moves within the buffer the strip slides; once the slide has
     finished (or when the jump is bigger than the buffer) the strip is
     re-based on the new centre with the transition switched off for that
     frame, so nothing visibly moves. */
  const [selected, setSelected] = useState(() => startOfDay(Date.now()));
  const [railCenter, setRailCenter] = useState(() => startOfDay(Date.now()));
  const [renderCenter, setRenderCenter] = useState(railCenter);
  const skipAnimRef = useRef(false);
  const [, forceRailRender] = useState(0);

  const offsetDays = moment(railCenter).diff(moment(renderCenter), "days");

  useEffect(() => {
    if (offsetDays === 0) return undefined;
    if (Math.abs(offsetDays) > RAIL_BUFFER) {
      skipAnimRef.current = true;
      setRenderCenter(railCenter);
      return undefined;
    }
    const t = setTimeout(() => {
      skipAnimRef.current = true;
      setRenderCenter(railCenter);
    }, RAIL_SLIDE_MS + 20);
    return () => clearTimeout(t);
  }, [railCenter, renderCenter, offsetDays]);

  useEffect(() => {
    if (!skipAnimRef.current) return;
    const id = requestAnimationFrame(() => {
      skipAnimRef.current = false;
      forceRailRender((n) => n + 1);
    });
    return () => cancelAnimationFrame(id);
  }, [renderCenter]);

  const days = useMemo(
    () =>
      Array.from({ length: RAIL_DAYS + 2 * RAIL_BUFFER }, (_, i) => {
        const stamp = shiftDays(renderCenter, i - RAIL_HALF - RAIL_BUFFER);
        const m = moment(stamp);
        return {
          stamp,
          iso: m.format(DATE_FMT),
          dow: m.format("ddd"),
          num: m.date(),
          mon: m.format("MMM"),
          selected: stamp === selected,
        };
      }),
    [renderCenter, selected]
  );

  const [calOpen, setCalOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => monthOf(selected));
  const calRef = useRef(null);
  const [type, setType] = useState(
    ["Funeral", "Retail"].includes(params.get("type")) ? params.get("type") : "All"
  );
  const [fdFilter, setFdFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("Any");
  /* `query` is what is typed, `search` what was submitted — the list only
     refetches when the search is actually run, as the legacy list did. */
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [runMode, setRunMode] = useState(
    params.get("run") === "1" || Boolean(params.get("editRun"))
  );
  /* Editing a saved run (?editRun=<id>, from Delivery runs → Edit): run
     mode on the run's day with its stops already picked, and the popped-out
     builder edits that run instead of starting a new one. */
  const [editRunId, setEditRunId] = useState(params.get("editRun") || "");
  const [editRun, setEditRun] = useState(null); // the saved run document
  const [editSeeded, setEditSeeded] = useState(false);
  /* the run's deliveries that match no row on the list (an order since
     moved or deleted) — sent on untouched so editing never drops them */
  const editOrphans = useRef([]);
  const rowCache = useRef({}); // every row seen, so a search can't drop a pick
  const skippedRef = useRef(0);
  const [picked, setPicked] = useState({});
  /* the "Casket cover or Tribute?" bubble at the click point, and the
     "can't add that" notice — both only exist in run mode */
  const [typePrompt, setTypePrompt] = useState(null);
  const [runAlert, setRunAlert] = useState(null);
  const [openOrder, setOpenOrder] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [orders, setOrders] = useState([]);
  /* what `orders` holds: a DD/MM/YYYY day, or "search:<text>" */
  const [loadedFor, setLoadedFor] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [settings, setSettings] = useState({});
  /* chips group by settings cc_types — picks are chip indexes, so an edit
     is only seeded once the grouping is final */
  const [settingsReady, setSettingsReady] = useState(false);
  const [statusMap, setStatusMap] = useState({});
  const [dayCounts, setDayCounts] = useState({});
  const [existingRuns, setExistingRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const listDate = moment(selected).format(DATE_FMT);
  const ccTypes = useMemo(
    () => casketTypesOf(settings),
    [settings]
  );
  const wantedOrderSerial = useRef(params.get("order") || "");

  useEffect(() => {
    axios
      .get(BASE_SERVER_URL + "/settings/getAll")
      .then((doc) => {
        if (doc.data.status === "success") setSettings(doc.data.response[0] || {});
      })
      .catch(() => {})
      .finally(() => setSettingsReady(true));
  }, []);

  /* Leaving run mode always drops the selection — the old page kept it in
     localStorage and it went stale. */
  useEffect(() => {
    if (!runMode) {
      setPicked({});
      setTypePrompt(null);
      setRunAlert(null);
    }
  }, [runMode]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setTypePrompt(null);
      setRunAlert(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  /* Calendar popover: close on outside click or Escape. */
  useEffect(() => {
    if (!calOpen) return undefined;
    const onDown = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setCalOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [calOpen]);

  const selectedStamp = selected;

  function pickDate(stamp) {
    /* A run sheet is one day's — while one is being edited, the list
       stays on its day. */
    if (editRun && moment(stamp).format(DATE_FMT) !== editRun.delivery_date) {
      toast.info(
        `This run sheet is for ${editRun.delivery_date} — save or cancel it to change day.`
      );
      return;
    }
    /* A search spans every date, so choosing a day is how you leave it. */
    setQuery("");
    setSearch("");
    setSelected(stamp);
    setRailCenter(stamp);
    setCalOpen(false);
  }

  const refreshList = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    const url = BASE_SERVER_URL + "/wo/getAllBy/";
    const base = { page: 1, byQuery: search, limit: 100, sortDirection: -1 };
    /* Searching drops the date filter — a keyword is only useful if it
       reaches orders outside the open day. Results come back newest
       funeral / delivery date first, so the current work sits on top as
       the history grows. The server sorts funerals by funeral_date_ts;
       retail ignores the sort key, so the merged list is sorted here. */
    Promise.all([
      axios.post(url, {
        ...base,
        filter: search ? { type: "Funeral" } : { type: "Funeral", funeral_date: listDate },
        sortBy: "funeral_date",
      }),
      axios.post(url, {
        ...base,
        filter: search ? { type: "Retail" } : { type: "Retail", delivery_date: listDate },
        sortBy: "delivery_date",
      }),
    ])
      .then(([funeral, retail]) => {
        const all = []
          .concat(
            funeral.data.status === "success" ? funeral.data.response : [],
            retail.data.status === "success" ? retail.data.response : []
          )
          .filter(Boolean);
        if (search) {
          const stamp = (o) => {
            const m = moment(
              o.type === "Retail" ? o.delivery_date : o.funeral_date,
              DATE_FMT,
              true
            );
            return m.isValid() ? m.valueOf() : 0;
          };
          all.sort((a, b) => stamp(b) - stamp(a));
        }
        setOrders(all);
        setLoadedFor(search ? `search:${search}` : listDate);
        /* Search results come from every date — they say nothing about how
           many orders the selected day holds, so the rail keeps its count. */
        if (!search) setDayCounts((prev) => ({ ...prev, [listDate]: all.length }));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
  }, [listDate, search]);

  useEffect(refreshList, [refreshList]);

  /* Row status — same per-row POST /wo/check-complete the legacy list
     made; the only order-level state the API exposes. */
  useEffect(() => {
    let live = true;
    orders.forEach((o) => {
      if (statusMap[o._id] !== undefined) return;
      axios
        .post(BASE_SERVER_URL + "/wo/check-complete", {
          id: o._id,
          type: o.type,
          date: o.type === "Retail" ? o.delivery_date : null,
        })
        .then((doc) => {
          if (!live) return;
          setStatusMap((prev) => ({
            ...prev,
            [o._id]: doc.data.status === "complete" ? "complete" : "open",
          }));
        })
        .catch(() => {});
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  /* Run sheets already saved for this day — needed in selection mode to
     strike through committed products and to show the run cards. */
  const refreshRuns = useCallback(() => {
    setRunsLoading(true);
    /* The whole run documents, not /run/getAllAddedProducts — that endpoint
       only reports which product *groups* an order has on a run, which is
       too coarse to tell an allocated product from its neighbour in the
       same group. The deliveries here carry their product lists. */
    axios
      .post(BASE_SERVER_URL + "/run/getAllBy", {
        page: 1,
        byQuery: "",
        limit: 200,
        sortBy: "delivery_date",
        find: { delivery_date: listDate },
      })
      .then((doc) => {
        const list = doc.data.status === "success" ? doc.data.response || [] : [];
        setExistingRuns(
          list.map((run) => ({
            id: run._id,
            runDetails: {
              id: run._id,
              drivers_name: run.drivers_name,
              vehicle_number: run.vehicle_number,
              comment: run.comment,
              description: run.description,
              allShort: (run.deliveries || []).flatMap((d) =>
                safeParse(d.shortened, []) || []
              ),
            },
            deliveries: run.deliveries || [],
          }))
        );
        setRunsLoading(false);
      })
      .catch(() => setRunsLoading(false));
  }, [listDate]);

  useEffect(() => {
    if (runMode) refreshRuns();
  }, [runMode, refreshRuns]);

  /* The builder tab announces a save through localStorage. */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "rf-run-saved") {
        setRunMode(false);
        setPicked({});
        forgetRunBuilder();
        leaveEdit();
        toast.success("Run sheet saved");
        refreshRuns();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshRuns]);

  /* taken[woId] = Map(takenKey -> { color, driver }) — one entry per
     product already committed to a saved run, so a product can only ever
     be allocated once, while the rest of its group stays pickable. */
  const taken = useMemo(() => {
    const out = {};
    existingRuns.forEach((run, i) => {
      /* the run being edited is the selection itself, not a clash */
      if (run.id === editRunId) return;
      const info = {
        color: runColor(i),
        driver: (run.runDetails || {}).drivers_name || "",
      };
      (run.deliveries || []).forEach((d) => {
        const woId = d.work_order_id;
        if (!woId) return;
        out[woId] = out[woId] || new Map();
        const products = safeParse(d.products, []) || [];
        if (products.length) {
          products.forEach((prod) =>
            out[woId].set(takenKey(d.product_type, productKey(prod)), info)
          );
        } else {
          out[woId].set(takenKey(d.product_type, "*"), info);
        }
      });
    });
    return out;
  }, [existingRuns, editRunId]);

  /* API order, numbered per section — the same order the legacy list showed. */
  const allRows = useMemo(() => orders.map((o) => buildRow(o, ccTypes)), [orders, ccTypes]);

  useEffect(() => {
    allRows.forEach((r) => {
      rowCache.current[r.id] = r;
    });
  }, [allRows]);

  /* Only setters and refs — safe to call from a listener set up on an
     earlier render. */
  function leaveEdit() {
    setEditRunId("");
    setEditRun(null);
    setEditSeeded(false);
    editOrphans.current = [];
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("editRun");
        return next;
      },
      { replace: true }
    );
  }

  /* Editing: fetch the run, then move the list to its day. */
  useEffect(() => {
    if (!editRunId) return undefined;
    let live = true;
    /* a draft left by an earlier session must not reach the builder */
    localStorage.removeItem(DRAFT_KEY);
    axios
      .get(BASE_SERVER_URL + "/run/getRun/" + editRunId)
      .then((doc) => {
        if (!live) return;
        const run = doc.data.status === "success" && (doc.data.response || [])[0];
        if (!run) {
          toast.error("That run sheet could not be found.");
          setRunMode(false);
          leaveEdit();
          return;
        }
        setEditRun(run);
        const day = moment(run.delivery_date, DATE_FMT, true);
        if (day.isValid()) {
          const stamp = startOfDay(day.valueOf());
          setQuery("");
          setSearch("");
          setSelected(stamp);
          setRailCenter(stamp);
        }
      })
      .catch(() => live && toast.error("Couldn't load the run sheet to edit."));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRunId]);

  /* …and once that day's orders are in, pick every product already on the
     run — the same product match the taken map uses. A delivery whose
     order isn't on the list is kept aside, whole. */
  useEffect(() => {
    if (!editRun || editSeeded || !settingsReady) return;
    if (loadedFor !== editRun.delivery_date) return;
    const next = {};
    const orphans = [];
    (editRun.deliveries || []).forEach((d) => {
      const products = safeParse(d.products, []) || [];
      const keys = products.length
        ? products.map((p) => takenKey(d.product_type, productKey(p)))
        : [takenKey(d.product_type, "*")];
      const row = allRows.find((r) => r.id === d.work_order_id);
      const hits = row
        ? row.chips.filter(
            (c) =>
              keys.includes(takenKey(c.group, c.key)) ||
              keys.includes(takenKey(c.group, "*"))
          )
        : [];
      if (!hits.length) {
        orphans.push({ ...d, products });
        return;
      }
      next[row.id] = [
        ...new Set([...(next[row.id] || []), ...hits.map((c) => c.index)]),
      ];
    });
    editOrphans.current = orphans;
    setPicked(next);
    setEditSeeded(true);
  }, [editRun, editSeeded, settingsReady, loadedFor, allRows]);

  const fdOptions = useMemo(
    () => [...new Set(allRows.map((r) => r.fdCode).filter(Boolean))].sort(),
    [allRows]
  );

  const rows = useMemo(
    () =>
      allRows.filter(
        (r) =>
          (type === "All" || r.type === type) &&
          (fdFilter === "All" || r.fdCode === fdFilter) &&
          (statusFilter === "Any" ||
            (STATUS_META[statusMap[r.id]] || {}).label === statusFilter)
      ),
    [allRows, type, fdFilter, statusFilter, statusMap]
  );

  /* Funeral work orders that share a parent funeral form one card, as the
     legacy list merged them by parent_funeral_id. Retail stays one per card. */
  const groups = useMemo(() => {
    const out = [];
    const byKey = {};
    rows.forEach((r) => {
      const key =
        r.type === "Funeral" && r.raw.parent_funeral_id
          ? "p:" + r.raw.parent_funeral_id
          : r.id;
      if (!byKey[key]) {
        byKey[key] = { key, type: r.type, rows: [] };
        out.push(byKey[key]);
      }
      byKey[key].rows.push(r);
    });
    return out;
  }, [rows]);

  const counts = useMemo(
    () => ({
      All: allRows.length,
      Funeral: allRows.filter((r) => r.type === "Funeral").length,
      Retail: allRows.filter((r) => r.type === "Retail").length,
    }),
    [allRows]
  );

  /* Deep link: /work-order/:type/:serial redirects here with ?order= */
  useEffect(() => {
    if (!wantedOrderSerial.current || loading) return;
    const hit = allRows.find((r) => r.serial === wantedOrderSerial.current);
    if (hit) openPanel(hit);
    wantedOrderSerial.current = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, allRows]);

  const chipTaken = (row, chip) => {
    const map = taken[row.id];
    if (!map) return undefined;
    return (
      map.get(takenKey(chip.group, chip.key)) ||
      map.get(takenKey(chip.group, "*"))
    );
  };

  const onRunSheetOf = (takenBy) =>
    takenBy.driver ? `${takenBy.driver}'s run sheet` : "an earlier run sheet";

  function toggleChip(row, index) {
    const chip = row.chips[index];
    const takenBy = chipTaken(row, chip);
    if (takenBy) {
      setRunAlert({
        title: "Can't add to the run sheet",
        message: `${chip.label} for ${row.name} is already on ${onRunSheetOf(
          takenBy
        )}. Take it off that run first if it belongs on this one.`,
      });
      return;
    }
    if (!groupHasLocation(row.raw, chip.group)) {
      setRunAlert(noLocationAlert([row], chip.group));
      return;
    }
    setPicked((prev) => {
      const list = prev[row.id] || [];
      const next = list.includes(index)
        ? list.filter((i) => i !== index)
        : [...list, index];
      return { ...prev, [row.id]: next };
    });
  }

  /* One product group of one or more orders — what the type bubble adds,
     and what a retail row adds straight away. Mirrors the legacy list:
     a whole group goes on at once, and if none of it can, the notice says
     why (nothing saved, already on another run, already picked here). */
  /* The notice for a group that has no delivery location saved. Nothing
     from that group goes on — not even the orders that do have one — so
     the list never shows a product as loaded that the run sheet dropped. */
  function noLocationAlert(rowsOf, group) {
    const what = GROUP_LABEL[group] || "product";
    const name = rowsOf[0].name;
    const n = rowsOf.length;
    return {
      title: "Can't add to the run sheet",
      message:
        n > 1
          ? `${n} work orders on ${name}'s funeral have no ${what} delivery location. Open each one and choose where the ${what}s go, then add them.`
          : `${name}'s ${what}s have no delivery location. Open the work order and choose where they go, then add them.`,
    };
  }

  function addGroup(rowsOf, group) {
    setTypePrompt(null);
    const missing = rowsOf.filter(
      (row) =>
        row.chips.some((c) => c.group === group) && !groupHasLocation(row.raw, group)
    );
    if (missing.length) {
      setRunAlert(noLocationAlert(missing, group));
      return;
    }
    const free = [];
    let total = 0;
    let takenBy = null;
    rowsOf.forEach((row) => {
      row.chips.forEach((chip, i) => {
        if (chip.group !== group) return;
        total += 1;
        const t = chipTaken(row, chip);
        if (t) {
          takenBy = takenBy || t;
          return;
        }
        if ((picked[row.id] || []).includes(i)) return;
        free.push({ id: row.id, index: i });
      });
    });

    if (free.length) {
      setPicked((prev) => {
        const next = { ...prev };
        free.forEach(({ id, index }) => {
          next[id] = [...(next[id] || []), index];
        });
        return next;
      });
      return;
    }

    const what = GROUP_LABEL[group] || "";
    setRunAlert({
      title: "Can't add to the run sheet",
      message:
        total === 0
          ? `No ${what} products are saved under this work order.`
          : takenBy
          ? `Those ${what} products are already on ${onRunSheetOf(takenBy)}.`
          : `Those ${what} products are already on the run sheet you are building.`,
    });
  }

  /* Clicking an order in run mode: a funeral asks which half of the order
     is going on the run, right where it was clicked; a retail order has
     only the one group, so it goes straight on. */
  function pickFromRow(rowsOf, event) {
    const row = rowsOf[0];
    if (row.type !== "Funeral") {
      addGroup(rowsOf, "all");
      return;
    }
    /* The bubble stands above the click with its tip on it, like the old
       list's; near the top of the window it flips below instead. */
    const above = event.clientY - PROMPT_H - 10 >= 8;
    setTypePrompt({
      rows: rowsOf,
      name: row.name,
      below: !above,
      x: Math.max(8, Math.min(event.clientX - PROMPT_W / 2, window.innerWidth - PROMPT_W - 8)),
      y: above ? event.clientY - PROMPT_H - 10 : event.clientY + 10,
    });
  }

  const pickedCount = useMemo(
    () => Object.values(picked).reduce((n, l) => n + l.length, 0),
    [picked]
  );
  const pickedStops = useMemo(
    () => Object.keys(picked).filter((k) => picked[k].length).length,
    [picked]
  );
  /* The run being built, summarised like a saved run's `allShort`:
     "2 x CC01", "3 x Wreath" across every picked order. */
  const pickedShort = useMemo(() => {
    const order = [];
    const sums = {};
    Object.keys(picked).forEach((woId) => {
      const row = allRows.find((r) => r.id === woId);
      if (!row) return;
      picked[woId].forEach((i) => {
        const chip = row.chips[i];
        if (!chip) return;
        if (!(chip.key in sums)) {
          sums[chip.key] = 0;
          order.push(chip.key);
        }
        sums[chip.key] += chip.amount;
      });
    });
    return order.map((k) => `${sums[k]} x ${k}`);
  }, [picked, allRows]);

  const clashCount = useMemo(
    () =>
      allRows.reduce(
        (n, r) => n + r.chips.filter((c) => chipTaken(r, c)).length,
        0
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRows, taken]
  );

  /* The selection as ready-made delivery elements — the same shape
     POST /run/AddNew wants (products still arrays here). */
  function buildDraft() {
    const deliveries = [];
    let skipped = 0;
    Object.keys(picked).forEach((woId) => {
      /* editing keeps its picks through a search; a new run, as before,
         only takes what the open day lists */
      const row =
        allRows.find((r) => r.id === woId) ||
        (editRunId ? rowCache.current[woId] : null);
      if (!row || !picked[woId].length) return;
      const o = row.raw;
      const byGroup = {};
      picked[woId].forEach((i) => {
        const chip = row.chips[i];
        if (!chip) return;
        byGroup[chip.group] = byGroup[chip.group] || [];
        chip.products.forEach((p) =>
          byGroup[chip.group].push({ ...p, work_order_id: woId })
        );
      });
      const GROUPS = {
        "casket cover": {
          product_type: "Casket Cover",
          time: o.casket_cover_delivery_time || o.funeral_time || "",
          location: GROUP_LOCATION["casket cover"](o),
        },
        tributes: {
          product_type: "Tributes",
          /* the arrival time typed on the order; the service time is only
             a fallback for orders saved before that field existed */
          time: o.tributes_delivery_time || o.funeral_time || "",
          location: GROUP_LOCATION.tributes(o),
        },
        all: {
          product_type: "Flower Arrangements",
          time: o.time_of_delivery || "",
          location: GROUP_LOCATION.all(o),
        },
      };
      Object.keys(byGroup).forEach((g) => {
        const spec = GROUPS[g];
        if (!spec || !locName(spec.location) || !byGroup[g].length) {
          skipped += byGroup[g] ? byGroup[g].length : 0;
          return;
        }
        deliveries.push({
          ...(row.type === "Funeral"
            ? {
                fd_code: o.fd_code,
                late_person_name: o.late_person_name,
                /* stored on the delivery, printed beside the destination */
                funeral_time: o.funeral_time || "",
              }
            : {}),
          delivery_date: listDate,
          work_order_id: woId,
          parent_funeral_id: o.parent_funeral_id,
          order_type: o.type,
          name:
            row.type === "Funeral"
              ? o.late_person_name
              : o.recipient_name || o.company_name,
          product_type: spec.product_type,
          time: spec.time,
          location: spec.location,
          products: byGroup[g],
        });
      });
    });
    return { deliveries, skipped };
  }

  /* Like the legacy list: "Make run sheet" pops the builder out in its
     own window straight away, and every pick made here streams into it.
     localStorage (not sessionStorage) because only its `storage` event
     reaches the other window. */
  function openBuilder() {
    const win = openRunBuilder(
      editRunId ? `/run-sheet/${editRunId}/edit` : "/run-sheet/new"
    );
    if (!win) {
      toast.error(
        "The run sheet window was blocked — allow pop-ups for this site, then press “Show run sheet”."
      );
    }
  }

  function toggleRunMode() {
    if (runMode) {
      setRunMode(false);
      setPicked({});
      localStorage.removeItem(DRAFT_KEY);
      closeRunBuilder();
      leaveEdit();
      return;
    }
    skippedRef.current = 0;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ date: listDate, deliveries: [] }));
    setRunMode(true);
    openBuilder();
  }

  useEffect(() => {
    if (!runMode) return;
    /* Editing: nothing goes out until the run's own stops are picked —
       the builder would read an empty selection and drop them. The run id
       rides along so only that run's builder follows this draft. */
    if (editRunId && !editSeeded) return;
    const { deliveries, skipped } = buildDraft();
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify(
        editRunId
          ? {
              date: listDate,
              runId: editRunId,
              deliveries: deliveries.concat(editOrphans.current),
            }
          : { date: listDate, deliveries }
      )
    );
    if (skipped > skippedRef.current) {
      toast.warn(
        `${skipped} product${skipped > 1 ? "s" : ""} skipped — no delivery location on the order.`
      );
    }
    skippedRef.current = skipped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runMode, picked, allRows, listDate, editRunId, editSeeded]);

  /* ---------------- detail panel ---------------- */
  function openPanel(row) {
    const status = statusMap[row.id];
    setOpenOrder({ ...row, statusColor: rowStatus(row).color, status: rowStatus(row).label, detail: buildDetail(row, null, status) });
    axios
      .get(BASE_SERVER_URL + "/wo/getProductsByOrderId/" + row.id)
      .then((doc) => {
        setOpenOrder((prev) =>
          prev && prev.id === row.id
            ? { ...prev, detail: buildDetail(row, doc.data.response || [], status) }
            : prev
        );
      })
      .catch(() => {});
  }

  function deleteOrder(row) {
    if (!window.confirm("Permanently delete this work order?")) return;
    setDeleteLoading(true);
    axios
      .get(BASE_SERVER_URL + "/wo/deleteWorkOrder/" + row.id)
      .then((doc) => {
        setDeleteLoading(false);
        if (doc.data.status === "success") {
          setOpenOrder(null);
          toast.success("Work order deleted");
          refreshList();
        }
      })
      .catch(() => setDeleteLoading(false));
  }

  /* The preview's "Paid" button. Only the two payment fields change, so the
     open order and its list row are patched in place — no refetch. */
  function markPaid(row, method, date) {
    return axios
      .post(BASE_SERVER_URL + "/wo/markPaid", {
        id: row.id,
        payment_option: method,
        payment_date: date,
      })
      .then((doc) => {
        if (doc.data.status !== "success") throw new Error(doc.data.message);
        const patch = { payment_option: method, payment_date: date };
        setOrders((prev) =>
          prev.map((o) => (o._id === row.id ? { ...o, ...patch } : o))
        );
        setOpenOrder((prev) => {
          if (!prev || prev.id !== row.id) return prev;
          const fresh = buildRow({ ...prev.raw, ...patch }, ccTypes);
          return {
            ...prev,
            ...fresh,
            detail: {
              ...prev.detail,
              payment: buildDetail(fresh, null, statusMap[row.id]).payment,
            },
          };
        });
        toast.success(`Marked as paid · ${method} · ${date}`);
      })
      .catch((err) => {
        toast.error(
          (err && err.response && err.response.data && err.response.data.message) ||
            (err && err.message) ||
            "Could not mark the order as paid"
        );
        throw err;
      });
  }

  const rowStatus = (row) =>
    STATUS_META[statusMap[row.id]] || { label: "…", color: "#e0dacd" };

  /* ---------------- row rendering ---------------- */
  const invoiceCell = (r) =>
    r.unpaid ? (
      <span className="wo-label is-red">UNPAID</span>
    ) : (
      <>
        {r.invoice}
        {r.paid && <span className="wo-label is-green">PAID</span>}
      </>
    );

  const renderChip = (r, c, readOnly) => {
    const list = picked[r.id] || [];
    const takenBy = !readOnly && chipTaken(r, c);
    const isTaken = Boolean(takenBy);
    const isOn = !readOnly && list.includes(c.index);
    const state = isTaken ? "taken" : isOn ? "on" : "off";
    return (
      <span
        key={c.index !== undefined ? c.index : c.key}
        className={`wo-prod is-${state}`}
        /* painted in its run's colour, the same as that run's card */
        style={
          isTaken
            ? { background: takenBy.color, borderColor: takenBy.color }
            : undefined
        }
        onClick={(e) => {
          if (!runMode || readOnly) return;
          e.stopPropagation();
          toggleChip(r, c.index);
        }}
        title={
          isTaken
            ? `Already on ${takenBy.driver || "another"}'s run`
            : undefined
        }
      >
        {runMode && !readOnly && (
          <span
            className="wo-prod-mark"
            style={isTaken ? { color: takenBy.color } : undefined}
          >
            {isTaken ? "Loaded" : isOn ? "On run" : "○"}
          </span>
        )}
        {c.label}
      </span>
    );
  };

  const productCard = (r, title, chips, readOnly) => (
    <div className="wo-prodcard" key={title}>
      <span className="wo-prodcard-title">{title}</span>
      <span className="wo-prodcard-tags">
        {chips.map((c) => renderChip(r, c, readOnly))}
      </span>
    </div>
  );

  /* Selection-mode check box for one order (or a whole funeral group). */
  const pickState = (rowsOf) => {
    let pickedN = 0;
    let availableN = 0;
    rowsOf.forEach((r) => {
      pickedN += (picked[r.id] || []).length;
      availableN += r.chips.filter((c) => !chipTaken(r, c)).length;
    });
    return pickedN === 0 ? "" : pickedN === availableN ? " is-all" : " is-some";
  };
  const pickBox = (rowsOf) => {
    const state = pickState(rowsOf);
    return (
      <span className="wo-col-pick">
        <span className={`wo-check${state}`}>
          {state === " is-all" ? "✓" : state === " is-some" ? "–" : ""}
        </span>
      </span>
    );
  };

  /* One card per funeral (all its work orders) or per retail order. A
     multi-order funeral shows the lead order's details with the combined
     products, and expands to one sub-row per order — the legacy list's
     parent/child rows. In selection mode it is always expanded so each
     order's chips can be picked. */
  function renderRow(group, num) {
    const r = group.rows[0];
    const multi = group.rows.length > 1;
    const funeral = r.type === "Funeral";
    const isOpen = multi && (runMode || expanded[group.key]);
    const complete = group.rows.every((x) => statusMap[x.id] === "complete");
    const active = openOrder && group.rows.some((x) => x.id === openOrder.id);

    const prodGroups = multi
      ? {
          cc: groupProducts(
            group.rows.flatMap((x) => x.groups.cc.flatMap((c) => c.products)),
            "casket cover"
          ),
          tributes: groupProducts(
            group.rows.flatMap((x) => x.groups.tributes.flatMap((c) => c.products)),
            "tributes"
          ),
        }
      : r.groups;

    const onCardClick = (e) => {
      if (runMode) {
        pickFromRow(group.rows, e);
        return;
      }
      if (multi) setExpanded((prev) => ({ ...prev, [group.key]: !prev[group.key] }));
      else openPanel(r);
    };

    return (
      <div
        key={group.key}
        className={`wo-row${active ? " is-active" : ""}${isOpen ? " is-open" : ""}`}
        onClick={onCardClick}
      >
        <div className="wo-cardwrap">
          <RowCurve />
          <div className={`wo-card${funeral ? "" : " is-retail"}`}>
            {/* identity block — spans both grid rows */}
            <div className="wo-ident">
              <div className="wo-line">
                {runMode && pickBox(group.rows)}
                <span className="wo-num rf-num">{num}</span>
                <span className="wo-name">{r.name}</span>
                {complete && (
                  <span className="wo-done" title="Complete">
                    <MdCheckCircle />
                  </span>
                )}
                {/* Moved here from another business — the serial keeps that
                    business's prefix, so say where it came from. */}
                {r.transferredFrom && (
                  <span
                    className="wo-from"
                    title={`Transferred from ${
                      r.transferredFrom.business_name ||
                      r.transferredFrom.business_code ||
                      "another business"
                    }`}
                  >
                    from {r.transferredFrom.business_code || r.transferredFrom.business_name}
                  </span>
                )}
                {multi && (
                  <span className={`wo-more${isOpen ? " is-open" : ""}`}>
                    {group.rows.length} orders
                    <MdExpandMore />
                  </span>
                )}
              </div>
              <div className="wo-line">
                {funeral ? (
                  <>
                    <Fact icon={<MdStorefront />} k="FD" v={r.fdCode} />
                    {!multi && (
                      <Fact icon={<MdPerson />} k="Ordered by" v={r.orderedBy} />
                    )}
                  </>
                ) : (
                  <>
                    <Fact icon={<MdPerson />} k="Order from" v={r.orderedBy} />
                    <Fact icon={<MdRepeat />} k="Frequency" v={r.frequency} />
                  </>
                )}
                {!multi && <Fact icon={<MdReceiptLong />} k="Invoice" v={invoiceCell(r)} />}
              </div>
            </div>

            {/* where / when — two rows, then the product cards on the right */}
            {funeral ? (
              <>
                <Fact icon={<MdAccessTime />} k="CC delivery time" v={r.ccTime} tone="time" />
                <Fact icon={<MdLocalShipping />} k="CC delivery" v={r.ccDelivery} tone="loc" />
                {productCard(r, "Casket cover", prodGroups.cc, multi)}
                <Fact icon={<MdEvent />} k="Funeral time" v={r.funeralWhen} tone="time" />
                <Fact icon={<MdChurch />} k="Funeral location" v={r.funeralLocation} tone="loc" />
                {productCard(r, "Tributes", prodGroups.tributes, multi)}
              </>
            ) : (
              <>
                <Fact icon={<MdAccessTime />} k="Delivery time" v={r.deliveryTime} tone="time" />
                <Fact icon={<MdLocationOn />} k="Delivery location" v={r.deliveryLocation} tone="loc" />
                {productCard(r, "Flowers", r.groups.all)}
                <span className="wo-pair">
                  <Fact icon={<MdPerson />} k="Recipient" v={r.recipientName} />
                  <Fact icon={<MdPhone />} k="Phone" v={r.recipientPhone} />
                </span>
                <Fact icon={<MdMap />} k="Area" v={r.deliveryArea} tone="loc" />
              </>
            )}
          </div>
        </div>

        {isOpen && (
          <div className="wo-subs">
            {group.rows.map((child, j) => (
              <div
                key={child.id}
                className={`wo-sub${
                  openOrder && openOrder.id === child.id ? " is-active" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (runMode) pickFromRow([child], e);
                  else openPanel(child);
                }}
              >
                <div className="wo-sub-ident">
                  {runMode && pickBox([child])}
                  <span className="wo-sub-num rf-num">
                    {num}.{j + 1}
                  </span>
                  <Fact k="Ordered by" v={child.orderedBy} />
                  <Fact k="Invoice" v={invoiceCell(child)} />
                  {statusMap[child.id] === "complete" && (
                    <span className="wo-done" title="Complete">
                      <MdCheckCircle />
                    </span>
                  )}
                </div>
                <span className="wo-sub-gap" />
                <div className="wo-sub-prods">
                  {productCard(child, "Casket cover", child.groups.cc)}
                  {productCard(child, "Tributes", child.groups.tributes)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const hyphenDate = replaceCharacter(listDate, "/", "-");

  function printDaySheet() {
    const funeralSheet = type !== "Retail";
    window.open(
      "/workorders/print/" +
        JSON.stringify({
          filter: funeralSheet
            ? { funeral_date: hyphenDate, type: "Funeral" }
            : { delivery_date: hyphenDate, type: "Retail" },
        }),
      "_blank"
    );
  }

  return (
    <div className="wo-page">
      {/* ---------- day rail ---------- */}
      <div className="wo-dayrail">
        <button
          type="button"
          className="wo-rail-arrow"
          aria-label="Earlier dates"
          onClick={() => setRailCenter((c) => shiftDays(c, -RAIL_STEP))}
        >
          <MdChevronLeft />
        </button>

        <div className="wo-rail-track">
          <div
            className="wo-rail-strip"
            style={{
              width: `${((RAIL_DAYS + 2 * RAIL_BUFFER) * 100) / RAIL_DAYS}%`,
              left: `${(-(RAIL_BUFFER + offsetDays) * 100) / RAIL_DAYS}%`,
              transition: skipAnimRef.current
                ? "none"
                : `left ${RAIL_SLIDE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`,
            }}
          >
            {days.map((d) => (
              <button
                key={d.iso}
                type="button"
                className={`wo-day${d.selected ? " is-active" : ""}`}
                onClick={() => pickDate(d.stamp)}
              >
                <span className="wo-day-dow">{d.dow}</span>
                <span className="wo-day-num rf-num">{d.num}</span>
                <span className="wo-day-sub rf-num">
                  {d.selected
                    ? d.mon
                    : dayCounts[d.iso] !== undefined
                    ? `${dayCounts[d.iso]} ord`
                    : ""}
                </span>
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="wo-rail-arrow"
          aria-label="Later dates"
          onClick={() => setRailCenter((c) => shiftDays(c, RAIL_STEP))}
        >
          <MdChevronRight />
        </button>
      </div>

      {/* ---------- run sheet context strip ---------- */}
      {runMode && (
        <div className="wo-runstrip">
          <div className="wo-runcard is-new" style={{ borderLeftColor: NEW_RUN_COLOR }}>
            <div className="wo-runcard-head">
              <span className="wo-runcard-driver">
                <MdLocalShipping className="wo-runcard-truck" style={{ color: NEW_RUN_COLOR }} />
                {editRunId
                  ? `Editing ${(editRun && editRun.drivers_name) || "run sheet"}`
                  : "Building run sheet"}
              </span>
              <span className="wo-runcard-vehicle" style={{ color: NEW_RUN_COLOR }}>
                {editRunId ? "EDIT" : "NEW"}
              </span>
            </div>
            <div className="wo-runcard-area rf-num">
              {pickedCount} products · {pickedStops} orders selected
            </div>
            <div className="wo-runcard-foot">
              {pickedShort.length === 0 && (
                <span className="wo-runcard-window">Pick products from the list below</span>
              )}
              {pickedShort.map((label) => (
                <span
                  key={label}
                  className="wo-runcard-prod rf-num"
                  style={{ background: NEW_RUN_COLOR }}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="wo-runstrip-existing">
            {!runsLoading &&
              existingRuns.map((r, i) => {
                /* the run being edited is the card on the left */
                if (r.id === editRunId) return null;
                const det = r.runDetails || {};
                const color = runColor(i);
                const short = det.allShort || [];
                return (
                  <div
                    key={det.id || i}
                    className="wo-runcard"
                    style={{ borderLeftColor: color }}
                  >
                    <div className="wo-runcard-head">
                      <span className="wo-runcard-driver">
                        <MdLocalShipping className="wo-runcard-truck" style={{ color }} />
                        {det.drivers_name || "—"}
                      </span>
                      <span className="wo-runcard-vehicle rf-num">
                        {det.vehicle_number || ""}
                      </span>
                    </div>
                    <div className="wo-runcard-area">
                      {[det.description, det.comment].filter(Boolean).join(" · ") || "—"}
                    </div>
                    {/* what is on the run — code/type x amount, in the run's colour */}
                    <div className="wo-runcard-foot">
                      {short.length === 0 && (
                        <span className="wo-runcard-window">No products</span>
                      )}
                      {short.map((label, k) =>
                        isExtra(label) ? (
                          <span
                            key={k}
                            className="wo-runcard-prod is-extra rf-num"
                            style={{ color, borderColor: color }}
                          >
                            {label}
                          </span>
                        ) : (
                          <span
                            key={k}
                            className="wo-runcard-prod rf-num"
                            style={{ background: color }}
                          >
                            {label}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            {!runsLoading &&
              existingRuns.every((r) => r.id === editRunId) && (
              <div className="wo-runcard">
                <div className="wo-runcard-area">
                  {editRunId
                    ? "No other run sheets on this day."
                    : "No run sheets saved for this day yet."}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- filter + actions ---------- */}
      <div className="wo-filters">
        <div className="rf-segmented">
          {["All", "Funeral", "Retail"].map((t) => (
            <button
              key={t}
              type="button"
              className={type === t ? "is-active" : ""}
              onClick={() => setType(t)}
            >
              <span>{t}</span>
              <span className="wo-tab-count rf-num">{counts[t]}</span>
            </button>
          ))}
        </div>

        <label className="wo-select">
          <span className="wo-select-k">FD</span>
          <select
            className="wo-select-native"
            value={fdFilter}
            onChange={(e) => setFdFilter(e.target.value)}
          >
            <option value="All">All directors</option>
            {fdOptions.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="wo-select">
          <span className="wo-select-k">Status</span>
          <select
            className="wo-select-native"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option>Any</option>
            <option>Complete</option>
            <option>Open</option>
          </select>
        </label>

        {/* jump-to-date — the shared overview calendar, single-day mode */}
        <div className="wo-cal-wrap" ref={calRef}>
          <button
            type="button"
            className={`wo-select wo-cal-btn${calOpen ? " is-open" : ""}`}
            aria-expanded={calOpen}
            onClick={() => {
              if (!calOpen) setViewMonth(monthOf(selectedStamp));
              setCalOpen((v) => !v);
            }}
          >
            <span className="wo-select-k">Date</span>
            <span className="wo-select-v rf-num">
              {moment(listDate, DATE_FMT).format("D MMM YYYY")}
            </span>
            <span className="wo-caret">▾</span>
          </button>

          {calOpen && (
            <div className="wo-cal-pop">
              <CalendarMonth
                year={viewMonth.year}
                monthIndex={viewMonth.monthIndex}
                from={selectedStamp}
                to={selectedStamp}
                onPick={pickDate}
                onPrev={() => setViewMonth((m) => shiftMonth(m, -1))}
                onNext={() => setViewMonth((m) => shiftMonth(m, 1))}
              />
            </div>
          )}
        </div>

        {/* keyword search — POST /wo/getAllBy/ byQuery, across all dates */}
        <form
          className="rf-search wo-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(query.trim());
          }}
        >
          <span className="rf-search-icon" />
          <input
            type="search"
            value={query}
            placeholder="Search work orders…"
            aria-label="Search work orders"
            onChange={(e) => setQuery(e.target.value)}
          />
          {(query || search) && (
            <button
              type="button"
              className="wo-search-clear"
              aria-label="Clear search"
              title="Clear search"
              onClick={() => {
                setQuery("");
                setSearch("");
              }}
            >
              <MdCancel />
            </button>
          )}
        </form>

        {search && (
          <span className="wo-sortnote">
            All dates, newest first · <b>{counts.All}</b> match
            {counts.All === 1 ? "" : "es"} · use the Funeral / Retail tabs to narrow
          </span>
        )}

        <span className="rf-spacer" />

        <button type="button" className="rf-btn rf-btn-ghost" onClick={printDaySheet}>
          Print
        </button>
        <button
          type="button"
          className={`rf-btn ${runMode ? "wo-btn-cancel" : "rf-btn-ghost"}`}
          onClick={toggleRunMode}
        >
          {runMode ? (editRunId ? "Cancel editing" : "Cancel run sheet") : "Make run sheet"}
        </button>
        <button
          type="button"
          className="rf-btn rf-btn-primary"
          onClick={() => navigate("/new-order")}
        >
          New order
        </button>
      </div>

      {/* ---------- tables: funeral then retail, like the legacy list ---------- */}
      <div className="wo-tablewrap">
        <div className="wo-tbody">
          {loading && <div className="rf-empty">Loading work orders…</div>}
          {!loading && loadError && (
            <div className="rf-empty">
              Couldn&apos;t load work orders.{" "}
              <button type="button" className="rf-btn rf-btn-ghost" onClick={refreshList}>
                Retry
              </button>
            </div>
          )}
          {!loading &&
            !loadError &&
            ["Funeral", "Retail"]
              .filter((k) => type === "All" || type === k)
              .map((kind) => {
                const list = groups.filter((g) => g.type === kind);
                return (
                  <section
                    key={kind}
                    className={`wo-section is-${kind.toLowerCase()}`}
                  >
                    <div className="wo-section-title">{kind} Orders</div>
                    {list.length === 0 && (
                      <div className="rf-empty">
                        {search ? (
                          <>
                            No {kind.toLowerCase()} work order matches “{search}”
                          </>
                        ) : (
                          <>
                            No {kind.toLowerCase()} work order found on{" "}
                            {moment(listDate, DATE_FMT).format("MMMM Do, YYYY")}
                          </>
                        )}
                      </div>
                    )}
                    {list.map((g, i) => renderRow(g, i + 1))}
                  </section>
                );
              })}
        </div>
      </div>

      {/* ---------- selection bar ---------- */}
      {runMode ? (
        <div className="wo-selectbar">
          <span className="wo-selectbar-count rf-num">{pickedCount}</span>
          <div>
            <div className="wo-selectbar-title">
              {pickedCount} products · {pickedStops} orders selected
            </div>
            <div className="wo-selectbar-note">
              {clashCount > 0
                ? `${clashCount} products are already on an earlier run — shown in that run's colour and not selectable.`
                : "Click a product chip to pick just that product, or the order to choose casket cover or tributes."}
            </div>
          </div>
          <span className="rf-spacer" />
          <button
            type="button"
            className="rf-btn rf-btn-ghost"
            onClick={() => setPicked({})}
          >
            Clear selection
          </button>
          <button
            type="button"
            className="rf-btn rf-btn-primary wo-build"
            onClick={openBuilder}
          >
            Show run sheet<span className="wo-build-hint">in its own window ↗</span>
          </button>
        </div>
      ) : (
        <div className="wo-pagepad" />
      )}

      {openOrder && (
        <OrderDetailPanel
          order={openOrder}
          onClose={() => setOpenOrder(null)}
          onPrint={() =>
            window.open(`/workorder/print/${openOrder.id}/${hyphenDate}`, "_blank")
          }
          onEdit={() =>
            navigate(
              `/editworkorder/${openOrder.type.toLowerCase()}/${openOrder.id}`
            )
          }
          onDelete={
            can(user, "workorders:delete") ? () => deleteOrder(openOrder) : undefined
          }
          deleteLoading={deleteLoading}
          onMarkPaid={
            can(user, "workorders:write")
              ? (method, date) => markPaid(openOrder, method, date)
              : undefined
          }
          canTransfer={can(user, "workorders:transfer")}
          /* An accepted transfer takes the order out of this business, so
             the list has to come back from the server either way. */
          onTransferChange={refreshList}
        />
      )}

      {/* ---------- "which half of the order?" bubble ---------- */}
      {typePrompt && (
        <>
          <div className="wo-prompt-scrim" onClick={() => setTypePrompt(null)} />
          <div
            className={`wo-prompt${typePrompt.below ? " is-below" : ""}`}
            style={{ left: typePrompt.x, top: typePrompt.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="wo-prompt-head">
              <span title={typePrompt.name}>Select Product Type</span>
              <button
                type="button"
                className="wo-prompt-x"
                aria-label="Close"
                onClick={() => setTypePrompt(null)}
              >
                <MdCancel />
              </button>
            </div>
            <div className="wo-prompt-opts">
              <button
                type="button"
                onClick={() => addGroup(typePrompt.rows, "casket cover")}
              >
                Casket Cover
              </button>
              <button
                type="button"
                onClick={() => addGroup(typePrompt.rows, "tributes")}
              >
                Tribute
              </button>
            </div>
            <span className="wo-prompt-tip" aria-hidden="true" />
          </div>
        </>
      )}

      {/* ---------- why that couldn't go on the run ---------- */}
      {runAlert && (
        <div className="wo-alert-wrap" onClick={() => setRunAlert(null)}>
          <div className="wo-alert-scrim" />
          <div className="wo-alert" onClick={(e) => e.stopPropagation()}>
            <div className="wo-alert-title">{runAlert.title}</div>
            <div className="wo-alert-msg">{runAlert.message}</div>
            <button
              type="button"
              className="rf-btn rf-btn-primary"
              onClick={() => setRunAlert(null)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkOrdersRedesign;
